import { assertNever } from "assert-never";
import * as monaco from "monaco-editor-core";
import React from "react";
import ReactDOM from "react-dom";
import { AiOutlineWarning } from "react-icons/ai";

import { Panic } from "../Errors/Panic";
import { CodeDiagnostic } from "../gen/CodeDiagnostics";
import { SrcSpan } from "../gen/SrcLoc";
import styles from "./CodeDiagnosticsPanel.module.css";

export interface CodeDiagnosticsPanelProps {
  codeDiagnostics: CodeDiagnostic[];
}

interface Editor {
  editorInstance: monaco.editor.IStandaloneCodeEditor;
  model: monaco.editor.ITextModel;
}

function CodeDiagnosticsPanel(props: CodeDiagnosticsPanelProps): JSX.Element {
  const codeDiagnosticsRef = React.useRef<CodeDiagnostic[]>(props.codeDiagnostics);
  codeDiagnosticsRef.current = props.codeDiagnostics;

  // const editorInstanceContRef = React.useRef<HTMLDivElement>();
  const diagnosticsEditorRef = React.useRef<DiagnosticsEditor>();

  const editorInstanceCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    console.log("editorInstanceCallbackRef", node);
    // if (editorInstanceContRef.current === node && diagnosticsEditorRef.current !== undefined) {
    //   console.log("*** UPDATE ***");
    //   diagnosticsEditorRef.current.update(codeDiagnosticsRef.current);
    // } else {
    // editorInstanceContRef.current = node;

    if (diagnosticsEditorRef.current !== undefined) {
      console.log("*** DISPOSE ***");
      diagnosticsEditorRef.current.dispose();
    }

    if (node === null) {
      diagnosticsEditorRef.current = undefined;
    } else {
      console.log("*** CREATE ***");
      diagnosticsEditorRef.current = new DiagnosticsEditor(node, codeDiagnosticsRef.current);
    }
    // }
  }, []);

  if (diagnosticsEditorRef.current !== undefined) {
    diagnosticsEditorRef.current.update(props.codeDiagnostics);
  }

  // const editorInstanceCallbackRef = React.useCallback((node: HTMLDivElement) => {
  //   console.log("editorInstanceCallbackRef", node);
  //   if (editorInstanceContRef.current === node && diagnosticsEditorRef.current !== undefined) {
  //     console.log("*** UPDATE ***");
  //     diagnosticsEditorRef.current.update(props.codeDiagnostics);
  //   } else {
  //     editorInstanceContRef.current = node;

  //     if (diagnosticsEditorRef.current !== undefined) {
  //       console.log("*** DISPOSE ***");
  //       diagnosticsEditorRef.current.dispose();
  //     }

  //     if (node === null) {
  //       diagnosticsEditorRef.current = undefined;
  //     } else {
  //       console.log("*** CREATE ***");
  //       diagnosticsEditorRef.current = new DiagnosticsEditor(node, props.codeDiagnostics);
  //     }
  //   }
  // }, [props.codeDiagnostics]);

  // React.useLayoutEffect(() => {
  //   console.log("editorInstanceContRef", editorInstanceContRef.current);
  //   if (editorInstanceContRef.current !== null) {
  //     const editorInstance = createMonacoEditor(editorInstanceContRef.current);
  //     editorInstanceRef.current = editorInstance;
  //     const model = editorInstanceRef.current.getModel();
  //     if (model === null) {
  //       throw new Panic("IStandaloneCodeEditor missing default model", {});
  //     }
  //     modelRef.current = model;
  //     // model.setValue(props.codeDiagnostics.map(c => c.filePath + "\n" + c.message).join("\n"));

  //     updateModel(model, [], initialCodeDiagnostics.current);

  //     // setPrevCodeDiagnostics(props.codeDiagnostics);

  //     return () => {
  //       console.log("DESTROYING");
  //       model.dispose();
  //       editorInstance.dispose();
  //     };
  //   }
  // }, [editorInstanceContRef]);

  // if (modelRef.current !== undefined) {
  //   modelRef.current.setValue("" + Math.random());
  // }

  return <div className={styles.cont} ref={editorInstanceCallbackRef} />;
}

export default CodeDiagnosticsPanel;

function createMonacoEditor(parent: HTMLElement): Editor {
  // "Is there a way to disable IntelliSense?" <https://github.com/microsoft/monaco-editor/issues/1681>
  const editorInstance = monaco.editor.create(parent, {
    language: CODE_DIAGNOSTICS_EDITOR_LANGUAGE_ID,
    // readOnly: true,
    lineNumbers: "off",
    renderIndentGuides: false,
    // glyphMargin: true,
    showFoldingControls: "always",
    foldingStrategy: "auto",
    matchBrackets: "never",
    selectionHighlight: false,
    tabSize: 2,
    automaticLayout: true,
    theme: "vs",
    scrollbar: {
      verticalHasArrows: true,
      useShadows: false,
      arrowSize: 15,
      horizontalScrollbarSize: 15,
      verticalScrollbarSize: 15
    },
    detectIndentation: false,
    autoClosingBrackets: "never",
    quickSuggestions: false,
    snippetSuggestions: "none",
    occurrencesHighlight: false,
    contextmenu: false,
    scrollBeyondLastLine: false,
    autoIndent: "keep",
    suggest: {
      showStatusBar: false,
      showSnippets: false
    },
    dragAndDrop: false,
    suggestOnTriggerCharacters: false,
    minimap: {
      enabled: false
    },
    renderLineHighlightOnlyWhenFocus: true
  });

  const model = editorInstance.getModel();
  if (model === null) {
    throw new Panic("IStandaloneCodeEditor missing default model", {});
  }

  return {
    editorInstance: editorInstance,
    model: model
  };
}

const CODE_DIAGNOSTICS_EDITOR_LANGUAGE_ID = "CODE_DIAGNOSTICS_EDITOR_LANGUAGE_ID";

function globalInit(): void {
  if (globalInitPerformed) {
    return;
  }
  globalInitPerformed = true;

  monaco.languages.register({
    id: CODE_DIAGNOSTICS_EDITOR_LANGUAGE_ID
  });

  console.log("registerFoldingRangeProvider");
  monaco.languages.registerFoldingRangeProvider(
    CODE_DIAGNOSTICS_EDITOR_LANGUAGE_ID,
    new DiagnosticsFoldingRangeProvider()
  );
}

let globalInitPerformed = false;

class DiagnosticsEditor implements monaco.IDisposable {
  constructor(parent: HTMLElement, codeDiagnostics: CodeDiagnostic[]) {
    globalInit();

    const editor = createMonacoEditor(parent);
    this.editorInstance = editor.editorInstance;
    this.model = editor.model;

    this.update(codeDiagnostics);
  }

  private readonly editorInstance: monaco.editor.IStandaloneCodeEditor;
  private readonly model: monaco.editor.ITextModel;

  private contentWidgets: monaco.editor.IContentWidget[] = [];

  dispose(): void {
    this.model.dispose();
    this.editorInstance.dispose();
  }

  update(codeDiagnostics: CodeDiagnostic[]): void {
    clearAllDecorations(this.model);
    for (const contentWidget of this.contentWidgets) {
      this.editorInstance.removeContentWidget(contentWidget);
    }
    this.contentWidgets = [];

    let newValue = "";

    let decorations: monaco.editor.IModelDeltaDecoration[] = [];
    let foldingRanges: monaco.languages.FoldingRange[] = [];

    let startLine = 1;
    for (const codeDiagnostic of codeDiagnostics) {
      const append = appendDiagnostic(startLine, codeDiagnostic);
      startLine += append.text.split("\n").length;
      newValue += append.text + "\n";

      decorations = decorations.concat(append.decorations);
      this.contentWidgets = this.contentWidgets.concat(append.contentWidgets);
      foldingRanges = foldingRanges.concat(append.foldingRanges);
    }

    this.model.setValue(newValue);
    // this.model.setValue(codeDiagnostics.map(c => c.filePath + "\n" + c.message).join("\n"));

    this.model.deltaDecorations([], decorations);

    for (const contentWidget of this.contentWidgets) {
      this.editorInstance.addContentWidget(contentWidget);
    }

    diagnosticsModelFoldingRanges.set(this.model, foldingRanges);

    // this.model.deltaDecorations([], [
    //   {
    //     range: new monaco.Range(3, 1, 3, 1),
    //     options: {
    //       isWholeLine: true,
    //       className: styles.file_name_line,
    //       glyphMarginClassName: styles.glyph_margin_error,
    //     }
    //   }
    // ]);
  }
}

class IconContentWidget implements monaco.editor.IContentWidget {
  static globalCount: number = 0;
  constructor(private readonly position: monaco.IPosition) {
    this.id = `CodeDiagnosticsPanel-IconContentWidget-${IconContentWidget.globalCount++}`;
  }

  private readonly id: string;

  allowEditorOverflow?: boolean | undefined;
  suppressMouseDown?: boolean | undefined;
  getId(): string {
    return this.id;
  }
  getDomNode(): HTMLElement {
    const div = document.createElement("div");
    div.className = styles.icon_cont;
    ReactDOM.render(<AiOutlineWarning size={19} color={"RED"} />, div);
    return div;
  }
  getPosition(): monaco.editor.IContentWidgetPosition | null {
    return {
      position: this.position,
      preference: [monaco.editor.ContentWidgetPositionPreference.EXACT]
    };
  }
}

function clearAllDecorations(model: monaco.editor.ITextModel): void {
  model.deltaDecorations(
    model.getAllDecorations().map((d) => d.id),
    []
  );
}

interface AppendResult {
  text: string;
  decorations: monaco.editor.IModelDeltaDecoration[];
  contentWidgets: monaco.editor.IContentWidget[];
  foldingRanges: monaco.languages.FoldingRange[];
}

/**
 * @param startLine is used for the decorations
 */
function appendDiagnostic(startLine: number, codeDiagnostic: CodeDiagnostic): AppendResult {
  let text = "";
  text += codeDiagnostic.filePath;
  if (codeDiagnostic.srcSpan !== null) {
    text += ":" + formatSrcSpan(codeDiagnostic.srcSpan);
  }
  text += "\n";
  const messageLines = codeDiagnostic.message.split("\n");
  const INDENT_SPACES = 4;
  text += messageLines.map((line) => " ".repeat(INDENT_SPACES) + line).join("\n");

  const filenameDecoration: monaco.editor.IModelDeltaDecoration = {
    range: new monaco.Range(startLine, 1, startLine, 1),
    options: {
      isWholeLine: true,
      className: styles.file_name_line
      // glyphMarginClassName: styles.glyph_margin_error,
    }
  };

  return {
    text: text,
    decorations: [filenameDecoration],
    contentWidgets: [
      new IconContentWidget({
        lineNumber: startLine + 1,
        column: 2
      })
    ],
    foldingRanges: [
      {
        start: startLine,
        end: startLine + messageLines.length
      }
    ]
  };
}

function formatSrcSpan(srcSpan: SrcSpan): string {
  switch (srcSpan.kind) {
    case "line":
      return `${srcSpan.value}`;
    case "lineCol":
      return `${srcSpan.value.line}:${srcSpan.value.col}`;
    case "lineRange":
      return `${srcSpan.value.startLine}`;
    case "lineColRange":
      if (srcSpan.value.start.line === srcSpan.value.end.line) {
        return `${srcSpan.value.start.line}:${srcSpan.value.start.col}-${srcSpan.value.end.col}`;
      } else {
        return `${srcSpan.value.start.line}:${srcSpan.value.start.col}-${srcSpan.value.end.line}:${srcSpan.value.end.col}`;
      }
    default:
      return assertNever(srcSpan);
  }
}

const diagnosticsModelFoldingRanges = new WeakMap<
  monaco.editor.ITextModel,
  monaco.languages.FoldingRange[]
>();

class DiagnosticsFoldingRangeProvider implements monaco.languages.FoldingRangeProvider {
  onDidChange?: monaco.IEvent<this> | undefined;

  provideFoldingRanges(
    model: monaco.editor.ITextModel
  ): monaco.languages.ProviderResult<monaco.languages.FoldingRange[]> {
    console.log("provideFoldingRanges");
    const foldingRanges = diagnosticsModelFoldingRanges.get(model);
    return foldingRanges === undefined ? [] : foldingRanges;
  }
}
